import { useQueries } from '@tanstack/react-query'

import type { SampleReport } from '@ctcells/contract'

import { orchestrator } from '../clients.ts'
import type { ApiError } from '../errors.ts'
import { keys } from '../keys.ts'

/**
 * 시료별 보고서를 **여러 건** 한 번에 — `GET /reports/{sample_id}` × N.
 *
 * 🔴 **왜 «여러 건» 훅이 따로 있나.** `useSampleReport`(단건)는 상세 화면용이다. 모니터링은
 *    도는 작업의 시료 전부를 동시에 그려야 하는데, 카드마다 훅을 부르면 카드 수가 바뀔 때
 *    훅 차례가 달라진다(React 규칙 위반). `useQueries` 가 **한 자리에서 N 개**를 쥔다.
 *
 * 🔴 **폴링이 아니다**(DP-01). `createQuery` 와 같은 규율로 `refetchInterval` 을 열지 않는다 —
 *    갱신은 구독(`sample_lifecycle`)이 `keys.reports` 를 무효화할 때만 온다.
 *    ⚠ `createQuery` 로 못 만드는 까닭은 그 생성자가 **훅 한 개**를 만들기 때문이다. 캐시 키는
 *      단건 훅과 **같은 `keys.reports.detail(id)`** 이라 두 훅이 같은 캐시를 나눠 쓴다.
 *
 * 🔴 **`sampleIds` 는 «도는 작업에 묶인 시료»만 넣는다.** 목록 전체(실측 111건)를 넣으면
 *    요청이 111개 나간다. 거르는 일은 부르는 쪽(모니터링)이 한다.
 *
 * ⚠ **권한**: `app/views/reports.py` `REPORT_ROLES = {operator, admin}` — **viewer 는 403
 *   `INSUFFICIENT_ROLE`** 이다(2026-09-22 실측). 부르는 쪽은 그 실패로 화면을 죽이지 말고
 *   **시각 칸만** 접어야 한다(「못 봤다」를 「없다」로 접지 마라).
 */
export interface SampleReportsArgs {
  /** 🔴 **도는 작업의 시료만.** 비면 요청이 한 건도 안 나간다 */
  sampleIds: readonly string[]
  /** `true` 면 한 건도 안 보낸다(데이터 소스가 `demo` · 권한 없음) */
  skip?: boolean
}

export interface SampleReportsResult {
  /** 받은 보고서. 🔴 **못 받은 시료는 아예 키가 없다** — 빈 값으로 채우지 않는다 */
  byId: ReadonlyMap<string, SampleReport>
  /** 아직 한 건이라도 오는 중 */
  isPending: boolean
  /** 🔴 **첫 실패 하나**. 전부 같은 권한 게이트를 지나므로 사유도 같다 */
  error: ApiError | undefined
}

export function useSampleReports(args: SampleReportsArgs): SampleReportsResult {
  const { sampleIds, skip = false } = args

  return useQueries({
    queries: sampleIds.map((sampleId) => ({
      queryKey: keys.reports.detail(sampleId),
      queryFn: () =>
        orchestrator.request<SampleReport>({ method: 'GET', path: `/reports/${sampleId}` }),
      enabled: !skip,
    })),
    combine: (results): SampleReportsResult => {
      const byId = new Map<string, SampleReport>()
      for (const result of results) {
        if (result.data !== undefined) byId.set(result.data.sample_id, result.data)
      }
      return {
        byId,
        isPending: results.some((result) => result.isPending && result.fetchStatus !== 'idle'),
        error: results.find((result) => result.error != null)?.error as ApiError | undefined,
      }
    },
  })
}
